import * as React from "react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import debounce from "lodash/debounce";
import { ClipboardButton, JSONClipboardButton, Link } from "components/common";
import OverlayTrigger from "components/common/OverlayTrigger";
import PaginatedList from "components/common/PaginatedList";
import Spinner from "components/common/Spinner";
import Routes from "routing/Routes";
import { Button, ButtonGroup, Input, ListGroup, ListGroupItem } from "components/bootstrap";
import SurroundingSearchButton from "components/search/SurroundingSearchButton";
import usePluginEntities from "hooks/usePluginEntities";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import MessagePermalinkButton from "views/components/common/MessagePermalinkButton";
import useFeature from "hooks/useFeature";
import useStreams from "components/streams/hooks/useStreams";
import MessageEditFieldConfigurationAction from "./fields/MessageEditFieldConfigurationAction";
const PAGE_SIZE = 10;
const TestAgainstStreamButton = ({ index, id }) => {
  const { t } = useTranslation("common");
  const sendTelemetry = useSendTelemetry("search-message-table");
  const [searchParams, setSearchParams] = useState({
    query: "",
    page: 1,
    pageSize: PAGE_SIZE,
    sort: { attributeId: "title", direction: "asc" }
  });
  const {
    data: { list: streams, pagination },
    isInitialLoading
  } = useStreams(searchParams);
  const sendEvent = () => sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_MESSAGE_TABLE_TEST_AGAINST_STREAM, {
    app_action_value: "seach-message-table-test-against-stream"
  });
  const handleSearch = debounce((value) => {
    setSearchParams((cur) => ({ ...cur, query: value, page: 1 }));
  }, 300);
  const handlePageChange = (newPage) => {
    setSearchParams((cur) => ({ ...cur, page: newPage }));
  };
  const popoverContent = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      formGroupClassName: "",
      placeholder: t("message.filter_streams"),
      onChange: ({ target: { value } }) => handleSearch(value)
    }
  ), isInitialLoading && /* @__PURE__ */ React.createElement(Spinner, null), /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      showPageSizeSelect: false,
      totalItems: pagination.total,
      hidePreviousAndNextPageLinks: true,
      hideFirstAndLastPageLinks: true,
      activePage: searchParams.page,
      pageSize: PAGE_SIZE,
      onChange: handlePageChange,
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(ListGroup, null, streams.map(
      (stream) => stream.is_default ? /* @__PURE__ */ React.createElement(ListGroupItem, { key: stream.id, disabled: true }, /* @__PURE__ */ React.createElement("span", { title: t("message.default_stream_not_testable") }, stream.title)) : /* @__PURE__ */ React.createElement(ListGroupItem, { key: stream.id }, /* @__PURE__ */ React.createElement(Link, { to: Routes.stream_edit_example(stream.id, index, id), onClick: sendEvent }, stream.title))
    ), !isInitialLoading && streams.length === 0 && /* @__PURE__ */ React.createElement(ListGroupItem, null, t("message.no_streams")))
  ));
  return /* @__PURE__ */ React.createElement(
    OverlayTrigger,
    {
      trigger: "click",
      placement: "bottom",
      overlay: popoverContent,
      title: t("message.test_against_stream"),
      rootClose: true
    },
    /* @__PURE__ */ React.createElement(Button, { bsSize: "small" }, t("message.test_against_stream"), " ", /* @__PURE__ */ React.createElement("span", { className: "caret" }))
  );
};
const usePluggableMessageActions = (id, index) => {
  const pluggableMenuActions = usePluginEntities("views.components.widgets.messageTable.messageActions");
  return pluggableMenuActions.filter((actions) => actions.useCondition ? !!actions.useCondition() : true).map(({ component: PluggableMenuAction, key }) => /* @__PURE__ */ React.createElement(PluggableMenuAction, { key, id, index }));
};
const MessageActions = ({
  index,
  id,
  fields,
  decorationStats,
  disabled,
  disableSurroundingSearch,
  disableTestAgainstStream,
  showOriginal,
  toggleShowOriginal
}) => {
  const { t } = useTranslation("common");
  const pluggableActions = usePluggableMessageActions(id, index);
  const isFavoriteFieldsEnabled = useFeature("message_table_favorite_fields");
  if (disabled) {
    return /* @__PURE__ */ React.createElement(ButtonGroup, null);
  }
  const { timestamp, ...remainingFields } = fields;
  const surroundingSearchButton = disableSurroundingSearch || /* @__PURE__ */ React.createElement(SurroundingSearchButton, { id, timestamp: String(timestamp), messageFields: remainingFields });
  const showChanges = decorationStats && /* @__PURE__ */ React.createElement(Button, { onClick: toggleShowOriginal, active: showOriginal }, t("message.show_changes"));
  return /* @__PURE__ */ React.createElement(ButtonGroup, null, showChanges, /* @__PURE__ */ React.createElement(MessagePermalinkButton, { messageIndex: index, messageId: id }), pluggableActions, /* @__PURE__ */ React.createElement(ClipboardButton, { title: t("message.copy_id"), text: id, bsSize: "small" }), /* @__PURE__ */ React.createElement(JSONClipboardButton, { title: t("message.copy_message"), bsSize: "small", content: fields }), surroundingSearchButton, disableTestAgainstStream ? null : /* @__PURE__ */ React.createElement(TestAgainstStreamButton, { id, index }), isFavoriteFieldsEnabled && /* @__PURE__ */ React.createElement(MessageEditFieldConfigurationAction, null));
};
export default MessageActions;
